import { FormEvent, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/gossip';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();
  const [draft, setDraft] = useState(keyword);

  const results = useMemo(() => {
    if (!keyword) return [];
    const lower = keyword.toLowerCase();
    return articles.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) ||
        item.excerpt.toLowerCase().includes(lower) ||
        item.categoryName.toLowerCase().includes(lower) ||
        item.tags.some((tag) => tag.toLowerCase().includes(lower)),
    );
  }, [keyword]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (value) setSearchParams({ q: value });
  };

  return (
    <>
      <Seo
        title={keyword ? `搜索“${keyword}” - 诱惑吃瓜` : '站内搜索 - 诱惑吃瓜'}
        description="在诱惑吃瓜搜索颜值美照、生图红毯、高光同框与造型解析内容，快速找到你想看的高清图集。"
        keywords="站内搜索,诱惑吃瓜,颜值美照,生图红毯,高光同框"
        robots="noindex, follow"
      />

      <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '站内搜索' }]} />

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1240px] px-4 md:px-8">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">站内搜索</h1>
          <p className="mt-3 text-sm text-foreground-600 md:text-base">
            输入关键词，例如“红毯生图”“同框构图”“香槟色礼服”，即可找到对应图集与解读。
          </p>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i
                className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="search"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="搜索美照、红毯、同框"
                aria-label="搜索关键词"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              开始搜索
            </button>
          </form>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门标签：</span>
            {['红毯生图', '同框构图', '自然光', '香槟色', '后台抓拍'].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  setDraft(tag);
                  setSearchParams({ q: tag });
                }}
                className="cursor-pointer whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-background-200 hover:text-foreground-900"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 py-12 md:px-8 md:py-16">
        {!keyword ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((category) => (
              <Link
                key={category.slug}
                to={`/category/${category.slug}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                  <i className={category.icon} aria-hidden="true"></i>
                </span>
                <div>
                  <p className="font-heading text-base font-semibold text-foreground-950">
                    {category.name}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">浏览该栏目</p>
                </div>
              </Link>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-600">
              <i className="ri-search-eye-line" aria-hidden="true"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              没有找到与“{keyword}”相关的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-600">试试更短的关键词，或者从下面的栏目进入。</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="rounded-full bg-background-50 px-4 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-200"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-foreground-600">
              关于 <span className="font-semibold text-foreground-900">“{keyword}”</span> 共找到{' '}
              <span className="font-semibold text-primary-600">{results.length}</span> 条结果
            </p>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}